import type { Platform } from "@aco/core";

export interface PlatformAdReference {
  platform: Platform;
  accountId?: string | null;
  businessId?: string | null;
  campaignId?: string | null;
  adGroupId?: string | null;
  adId: string;
}

export interface PlatformEntityReference {
  platform: Exclude<Platform, "google">;
  accountId?: string | null;
  campaignId?: string | null;
  adGroupId?: string | null;
}

function normalizedAccountId(accountId: string | null | undefined) {
  return accountId?.replace(/^act_/, "") ?? null;
}

// Snapchat has no verified Ads Manager deep link format, so it gets none.
export function platformCampaignUrl(reference: PlatformEntityReference): string | null {
  if (reference.platform === "snapchat") return null;
  const accountId = normalizedAccountId(reference.accountId);
  if (!accountId || !reference.campaignId) return null;
  if (reference.platform === "tiktok") {
    const params = new URLSearchParams({ aadvid: accountId, navigate_from: "other" });
    return `https://ads.tiktok.com/i18n/manage/campaign?${params.toString()}`;
  }
  const params = new URLSearchParams({
    act: accountId,
    treenav: "true",
  });
  return `https://adsmanager.facebook.com/adsmanager/manage/campaigns?${params.toString()}`;
}

export function platformAdGroupUrl(reference: PlatformEntityReference): string | null {
  if (reference.platform === "snapchat") return null;
  const accountId = normalizedAccountId(reference.accountId);
  if (!accountId || !reference.adGroupId) return null;
  if (reference.platform === "tiktok") {
    const params = new URLSearchParams({ aadvid: accountId, navigate_from: "other" });
    if (reference.campaignId) {
      params.set("filters[0][field]", "campaign_ids");
      params.set("filters[0][filter_type]", "0");
      params.set("filters[0][in_field_values][0]", reference.campaignId);
    }
    return `https://ads.tiktok.com/i18n/manage/adgroup?${params.toString()}`;
  }
  const params = new URLSearchParams({
    act: accountId,
    ...(reference.campaignId ? { selected_campaign_ids: reference.campaignId } : {}),
    treenav: "true",
  });
  return `https://adsmanager.facebook.com/adsmanager/manage/adsets?${params.toString()}`;
}

export function platformAdUrl(reference: PlatformAdReference): string | null {
  if (reference.platform === "google" || reference.platform === "snapchat") return null;
  const accountId = normalizedAccountId(reference.accountId);
  if (reference.platform === "tiktok") {
    if (!accountId) return null;
    const params = new URLSearchParams({ aadvid: accountId, navigate_from: "other" });
    if (reference.adGroupId) {
      params.set("filters[0][field]", "adgroup_ids");
      params.set("filters[0][filter_type]", "0");
      params.set("filters[0][in_field_values][0]", reference.adGroupId);
    }
    return `https://ads.tiktok.com/i18n/manage/creative?${params.toString()}`;
  }

  const params = new URLSearchParams();
  if (accountId) params.set("act", accountId);
  if (reference.businessId) {
    params.set("business_id", reference.businessId);
    params.set("global_scope_id", reference.businessId);
  }
  if (reference.adGroupId) params.set("selected_adset_ids", reference.adGroupId);
  if (reference.campaignId) params.set("selected_campaign_ids", reference.campaignId);
  params.set("treenav", "true");
  return `https://adsmanager.facebook.com/adsmanager/manage/ads?${params.toString()}`;
}
